import { z } from "zod";
import { GameTypeSchema } from "./games.js";
import { LocaleSchema } from "./i18n.js";
export const AddContactSchema = z.object({
    contactUserId: z.string().uuid().optional(),
    email: z.string().trim().toLowerCase().email().optional(),
    phone: z.string().min(1).optional(),
    alias: z.string().optional(),
}).refine(data => data.contactUserId || data.email || data.phone, {
    message: "contactUserId, email or phone required",
});
export const CreateSessionSchema = z.object({
    contactUserId: z.string().uuid(),
});
// US-M3 — pagination de l'historique. Par défaut on renvoie les tours les plus
// RÉCENTS (pas les plus anciens) ; `before` (curseur = startedAt ISO du plus
// ancien tour déjà rendu) permet de remonter dans le temps par pages.
export const SessionHistoryQuerySchema = z.object({
    before: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * Longueur maximale d'une adresse (RFC 5321 : 254 octets).
 *
 * Ce n'est pas de la cosmétique de schéma : la destination sert à FABRIQUER
 * des noms de clés Redis. Sans borne haute, une requête publique de 900 Ko
 * créait une clé de 900 Ko (audit du 25 juil. 2026, défaut CRITIQUE
 * « saturation permanente de Redis »). `z.string().email()` ne borne rien —
 * vérifié : il accepte une partie locale de 900 000 caractères.
 */
export const MAX_DESTINATION_LENGTH = 254;
const boundedEmail = () => z.string().trim().toLowerCase().max(MAX_DESTINATION_LENGTH).email();
const verificationDestination = z.string().trim().min(1).max(MAX_DESTINATION_LENGTH);
/** Impose le format e-mail quand le canal est « email » (le canal « sms » a
 *  ses propres destinataires, non validés ici). */
const withEmailDestination = (data, ctx) => {
    if (data.channel === "email" && !z.string().email().safeParse(data.destination).success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["destination"],
            message: "Invalid email address",
        });
    }
};
export const SendVerificationSchema = z
    .object({
    channel: z.enum(["email", "sms"]),
    destination: verificationDestination,
})
    .superRefine(withEmailDestination);
export const CheckVerificationSchema = z
    .object({
    channel: z.enum(["email", "sms"]),
    destination: verificationDestination,
    code: z.string().length(6),
})
    .superRefine(withEmailDestination);
export const VerifyEmailSchema = z.object({
    email: boundedEmail(),
    code: z.string().length(6),
});
export const ResendCodeSchema = z.object({
    email: boundedEmail(),
});
export const UpdateAccountSchema = z.object({
    displayName: z.string().min(2).optional(),
    phone: z.string().nullable().optional(),
    password: z.string().min(8).optional(),
    // Required by the server whenever `password` is present: re-authentication
    // for the sensitive password-change operation.
    currentPassword: z.string().optional(),
    instagram: z.string().nullable().optional(),
    tiktok: z.string().nullable().optional(),
    whatsapp: z.string().nullable().optional(),
    revealPhoneToContacts: z.boolean().optional(),
    revealEmailToContacts: z.boolean().optional(),
    showOnlineStatus: z.boolean().optional(),
    emailNotifications: z.boolean().optional(),
    // I18N-1 — même garde que le signup : locale normalisée puis validée, sinon 400.
    locale: LocaleSchema.optional(),
    /**
     * GA-6 — « mon jeu favori », choix explicite qui PRIME sur le calcul des 90
     * derniers jours. `null` remet le calcul automatique aux commandes.
     */
    favoriteGameType: GameTypeSchema.nullable().optional(),
});
export const RequestEmailChangeSchema = z.object({
    email: boundedEmail(),
});
export const VerifyEmailChangeSchema = z.object({
    email: boundedEmail(),
    code: z.string().length(6),
});
export const CreateReportSchema = z
    .object({
    /** Absent quand on signale un groupe entier plutôt qu'un de ses membres. */
    reportedUserId: z.string().uuid().optional(),
    reason: z.string().min(5),
    sessionId: z.string().uuid().optional(),
    /** Contexte groupe (G3a) : le groupe visé, ou celui où le membre a sévi. */
    groupId: z.string().uuid().optional(),
})
    .refine((d) => d.reportedUserId != null || d.groupId != null, {
    message: "reportedUserId or groupId is required",
    path: ["reportedUserId"],
})
    /**
     * UN SEUL CONTEXTE PAR SIGNALEMENT (défaut de la revue du 26 juil. 2026, qui
     * préexistait à ce lot).
     *
     * Accepter les deux produisait un dossier qui MENT : `groupId` posé, donc
     * l'écran d'admin affiche le nom du groupe et ses membres, mais le coffre est
     * capturé en `contextKind: "session"` et ne contient que le tête-à-tête privé —
     * le modérateur statue sur un groupe en lisant une AUTRE conversation. Pire, le
     * groupe entier devient un « parent gelé » pour le janitor des médias : la photo
     * d'un tiers, étranger au 1:1 comme à la plainte, échappe à la purge tant que le
     * dossier reste ouvert. Et (session), (groupe) et (session+groupe) comptaient
     * comme trois contextes distincts, donc trois dossiers et trois plafonds sur la
     * même paire de personnes.
     */
    .refine((d) => d.sessionId == null || d.groupId == null, {
    message: "sessionId and groupId are mutually exclusive",
    path: ["groupId"],
});
/**
 * CONTEXTE D'APPAREIL DU DÉPÔT — validé À PART du corps du signalement, et
 * volontairement.
 *
 * POURQUOI PAS DANS `CreateReportSchema`. Ces deux champs sont un CONFORT pour
 * la modération, pas la substance du signalement. Les mettre dans le schéma
 * principal ferait qu'un modèle d'appareil exotique de 200 caractères, ou une
 * plateforme que le client nommerait « ipados », renverrait un 400 : la personne
 * qui demande de l'aide se verrait refuser son signalement à cause d'une chaîne
 * de diagnostic. Ils sont donc parsés séparément et simplement IGNORÉS s'ils ne
 * passent pas — le dépôt aboutit, le dossier perd une indication de confort.
 *
 * L'adresse IP ne figure PAS ici : elle vient de `request.ip` côté serveur
 * (trustProxy + Caddy). Un client ne déclare pas sa propre adresse.
 */
export const ReportDeviceContextSchema = z.object({
    platform: z.enum(["ios", "android", "web"]).optional(),
    /** `Device.modelName` côté natif. Borné : c'est un libellé, pas un champ libre. */
    deviceModel: z.string().min(1).max(120).optional(),
});
export const ResolveReportSchema = z.object({
    status: z.enum(["OPEN", "REVIEWED", "ACTIONED", "DISMISSED"]),
});
export const BanUserSchema = z.object({
    reason: z.string().min(3),
});
export const BanDeviceSchema = z.object({
    reason: z.string().min(3),
});
export const RegisterDeviceSchema = z.object({
    token: z.string().min(1, "Le token ne doit pas être vide"),
    platform: z.enum(["ios", "android", "web"]),
    /**
     * AND-BC5 — version du binaire qui enregistre ce token de push.
     *
     * OPTIONNEL par construction, exactement comme `devicePlatform`
     * (packages/protocol/src/auth.ts) : un binaire déjà publié n'apprendra jamais
     * à l'envoyer, et le rendre obligatoire couperait TOUTES les versions
     * antérieures du push — définitivement, sans recours possible côté serveur.
     *
     * Le plafond de longueur est nécessaire (la valeur est écrite en base et lue
     * par la console), mais il ne doit RIEN refuser : `.catch(undefined)` fait
     * retomber toute valeur inattendue — trop longue, d'un autre type — sur
     * « version inconnue » au lieu de rejeter tout le corps en 400. Une
     * télémétrie ne casse jamais la fonction qu'elle observe : ici, le prix d'un
     * 400 serait la perte définitive du push pour ce binaire.
     */
    appVersion: z.string().trim().max(32).optional().catch(undefined),
});
/**
 * BE-9 — LISTE BLANCHE des services de push.
 *
 * POURQUOI (défaut de revue, SSRF) : l'`endpoint` d'un abonnement est l'URL que
 * l'API de production appellera, et il est PERSISTÉ. Accepter « n'importe quelle
 * URL https » laisserait n'importe quel compte authentifié choisir l'hôte vers
 * lequel la prod émet des requêtes sortantes — et le faire durer, une requête
 * par notification, avec notre JWT VAPID dans l'en-tête. Seuls les services de
 * push des navigateurs ont une raison d'apparaître ici.
 *
 * Un motif est soit un hôte exact, soit `*.suffixe` (un sous-domaine au moins).
 * Les régions changent chez Mozilla, Apple et Microsoft : d'où les jokers.
 */
export const DEFAULT_WEB_PUSH_HOSTS = [
    // Chrome / Chromium / Brave — FCM (et l'ancien endpoint GCM encore servi).
    "fcm.googleapis.com",
    "android.googleapis.com",
    // Firefox — autopush, régionalisé (updates.push.services.mozilla.com…).
    "*.push.services.mozilla.com",
    // Safari / iOS installé — APNs Web Push (web.push.apple.com).
    "*.push.apple.com",
    // Edge — WNS (wns2-xx.notify.windows.com).
    "*.notify.windows.com",
];
/**
 * Nom de la variable d'environnement qui REMPLACE la liste ci-dessus.
 *
 * Elle existe pour qu'un nouveau service de push (ou le retrait d'un service)
 * ne demande PAS un redéploiement de code : une valeur dans `.env.ionos` et un
 * redémarrage suffisent. Format : hôtes séparés par des virgules ou des
 * espaces, motifs `*.suffixe` acceptés. Vide ou absente = liste par défaut.
 */
export const WEB_PUSH_HOSTS_ENV = "WEB_PUSH_ALLOWED_HOSTS";
export function parseWebPushHostList(raw) {
    if (!raw)
        return [];
    return raw
        .split(/[,\s]+/)
        .map((h) => h.trim().toLowerCase())
        .filter((h) => h.length > 0);
}
/** Liste effective : la variable d'environnement si elle est posée, sinon le défaut. */
export function allowedWebPushHosts() {
    // `process` n'existe pas dans tous les hôtes de ce paquet (navigateur, RN) :
    // la lecture est défensive, et la validation qui compte est celle du serveur.
    const raw = typeof process !== "undefined" && process.env ? process.env[WEB_PUSH_HOSTS_ENV] : undefined;
    const configured = parseWebPushHostList(raw);
    return configured.length > 0 ? configured : DEFAULT_WEB_PUSH_HOSTS;
}
function hostMatches(host, pattern) {
    if (pattern.startsWith("*.")) {
        const suffix = pattern.slice(1); // ".push.apple.com"
        return host.length > suffix.length && host.endsWith(suffix);
    }
    return host === pattern;
}
/**
 * Vrai seulement pour un endpoint de service de push connu, en HTTPS, sans port
 * explicite ni identifiants dans l'URL (autant de façons de viser autre chose).
 */
export function isAllowedWebPushEndpoint(endpoint, hosts = allowedWebPushHosts()) {
    let url;
    try {
        url = new URL(endpoint);
    }
    catch {
        return false;
    }
    if (url.protocol !== "https:")
        return false;
    if (url.port !== "" || url.username !== "" || url.password !== "")
        return false;
    const host = url.hostname.toLowerCase();
    return hosts.some((pattern) => hostMatches(host, pattern));
}
/**
 * BE-9 — abonnement Web Push (navigateur). C'est la forme sérialisée d'un
 * `PushSubscription` du navigateur (`subscription.toJSON()`), pas un token :
 * l'`endpoint` est l'identité, `keys.p256dh`/`keys.auth` servent au chiffrement
 * RFC 8291. Les plafonds de longueur sont là pour qu'un client hostile ne
 * puisse pas écrire des mégaoctets dans la table.
 */
export const WebPushSubscribeSchema = z.object({
    endpoint: z
        .string()
        .url("L'endpoint doit être une URL")
        .max(1024)
        .refine((v) => v.startsWith("https://"), {
        message: "L'endpoint doit être en HTTPS",
    })
        // Liste blanche : c'est ce refus qui empêche la prod de devenir un émetteur
        // de requêtes sortantes piloté depuis un compte quelconque.
        .refine((v) => isAllowedWebPushEndpoint(v), {
        message: "L'endpoint n'appartient pas à un service de notifications connu",
    }),
    keys: z.object({
        p256dh: z.string().min(1).max(256),
        auth: z.string().min(1).max(256),
    }),
});
/**
 * BE-9 — désabonnement : l'endpoint suffit, il identifie l'abonnement.
 *
 * Volontairement SANS liste blanche : la suppression ne déclenche aucune requête
 * sortante, et une ligne enregistrée avant un resserrement de la liste doit
 * rester supprimable par son propriétaire.
 */
export const WebPushUnsubscribeSchema = z.object({
    endpoint: z.string().url().max(1024),
});
//# sourceMappingURL=rest.js.map